import { z } from "zod";
import { httpUrl } from "./url.js";

// 090 -- shop validation
// Payment is never collected; shipping + contact only.

export const cartItemUpsertSchema = z.object({
  provider_id: z.string().min(1, "Provider is required"),
  product_id: z.string().min(1, "Product is required"),
  quantity: z.number().int().min(0).max(999),
});

export type CartItemUpsertInput = z.infer<typeof cartItemUpsertSchema>;

export const shippingAddressSchema = z.object({
  line1: z.string().min(1, "Address line 1 is required").max(200),
  line2: z.string().max(200).nullish(),
  town: z.string().min(1, "Town is required").max(100),
  province: z.string().min(1, "Province is required").max(100),
  postal_code: z.string().min(1, "Postal code is required").max(20),
});

export type ShippingAddressInput = z.infer<typeof shippingAddressSchema>;

export const submitOrderSchema = z.object({
  provider_id: z.string().min(1, "Provider is required"),
  shipping_address: shippingAddressSchema,
  contact_phone: z.string().max(50).nullish(),
  notes: z.string().max(2000).nullish(),
  // 094 -- buyer-type + optional school_id. Defaults to 'parent' server-side.
  // school_id must be populated when buyer_type='school'.
  buyer_type: z.enum(["parent", "school"]).optional(),
  school_id: z.string().min(1).nullish(),
}).refine(
  (v) => v.buyer_type !== "school" || !!v.school_id,
  { message: "school_id is required when buyer_type='school'", path: ["school_id"] },
);

export type SubmitOrderInput = z.infer<typeof submitOrderSchema>;

// 094 -- PATCH /orders/:slug/status payload.
export const orderStatusSchema = z.enum(["submitted", "acknowledged", "fulfilled", "cancelled", "failed"]);

export const updateOrderStatusSchema = z.object({
  to_status: orderStatusSchema,
  reason: z.string().max(2000).nullish(),
}).refine(
  (v) => {
    // Require reason for destructive transitions.
    if (v.to_status === "cancelled" || v.to_status === "failed") {
      return typeof v.reason === "string" && v.reason.trim().length > 0;
    }
    return true;
  },
  { message: "A reason is required when cancelling or marking failed", path: ["reason"] },
);

export type UpdateOrderStatusInputParsed = z.infer<typeof updateOrderStatusSchema>;

// 092 + 093 -- admin provider/product write schemas.

const fulfillmentTypeSchema = z.enum(["order_email", "external_redirect", "quote", "order_api", "cashback"]);

// 095 -- order_api supported auth schemes. Each maps to a server-side dispatcher.
export const apiAuthSchemeSchema = z.enum(["bearer", "hmac_sha256", "basic"]);

// 093 -- QuoteFormField + QuoteFormConfig Zod schemas.
const quoteFormFieldTypeSchema = z.enum(["text", "email", "phone", "select", "textarea", "date"]);
const quoteFormFieldProfileKeySchema = z.enum([
  "first_name",
  "last_name",
  "phone_number",
  "email",
  "display_name",
]);

export const quoteFormFieldSchema = z.object({
  name: z.string().min(1).max(64).regex(/^[a-z_][a-z0-9_]*$/i, "Field name must be snake_case"),
  label: z.string().min(1).max(200),
  type: quoteFormFieldTypeSchema,
  required: z.boolean().optional(),
  options: z.array(z.string().min(1).max(200)).optional(),
  default_from_profile: quoteFormFieldProfileKeySchema.optional(),
  placeholder: z.string().max(200).optional(),
  max_length: z.number().int().min(1).max(10_000).optional(),
}).refine(
  (f) => f.type !== "select" || (f.options && f.options.length > 0),
  { message: "select fields must include options", path: ["options"] },
);

export const quoteFormConfigSchema = z.object({
  fields: z.array(quoteFormFieldSchema).min(1, "At least one field is required"),
});

export type QuoteFormConfigInput = z.infer<typeof quoteFormConfigSchema>;

const emptyToNull = z.preprocess(
  (v) => (typeof v === "string" && v.trim() === "" ? null : v),
  z.string().max(500).nullish(),
);

export const providerCreateSchema = z.object({
  name: z.string().min(1, "Name is required").max(200),
  slug: z.string().max(120).nullish(),
  fulfillment_type: fulfillmentTypeSchema,
  logo_url: emptyToNull,
  description: z.preprocess(
    (v) => (typeof v === "string" && v.trim() === "" ? null : v),
    z.string().max(5000).nullish(),
  ),
  discount_blurb: z.preprocess(
    (v) => (typeof v === "string" && v.trim() === "" ? null : v),
    z.string().max(200).nullish(),
  ),
  support_email: emptyToNull,
  fulfillment_email: emptyToNull,
  // 103 -- custom phrasing for the 'fulfilled' status-change email.
  fulfilled_email_copy: z.preprocess(
    (v) => (typeof v === "string" && v.trim() === "" ? null : v),
    z.string().max(2000).nullish(),
  ),
  external_url_template: z.preprocess(
    (v) => (typeof v === "string" && v.trim() === "" ? null : v),
    httpUrl(2000).nullish(),
  ),
  external_cta_label: z.preprocess(
    (v) => (typeof v === "string" && v.trim() === "" ? null : v),
    z.string().max(200).nullish(),
  ),
  // 093 -- quote fields. quote_form_config accepts either the structured object
  // (preferred) or null/undefined; cross-field refinement enforces presence.
  quote_form_config: quoteFormConfigSchema.nullish(),
  quote_submit_handler: z.preprocess(
    (v) => (typeof v === "string" && v.trim() === "" ? null : v),
    z.string().max(100).nullish(),
  ),
  // 095 -- order_api fields.
  api_endpoint_url: z.preprocess(
    (v) => (typeof v === "string" && v.trim() === "" ? null : v),
    httpUrl(2000).nullish(),
  ),
  api_auth_scheme: apiAuthSchemeSchema.nullish(),
  fulfilment_fallback_email: emptyToNull,
  active: z.boolean().optional(),
  sort_order: z.number().int().min(0).max(9999).optional(),
}).refine(
  (v) => v.fulfillment_type !== "external_redirect" || !!v.external_url_template,
  { message: "external_url_template is required for external_redirect providers", path: ["external_url_template"] },
).refine(
  (v) => v.fulfillment_type !== "order_email" || !!v.fulfillment_email,
  { message: "fulfillment_email is required for order_email providers", path: ["fulfillment_email"] },
).refine(
  (v) => v.fulfillment_type !== "quote" || !!v.quote_form_config,
  { message: "quote_form_config is required for quote providers", path: ["quote_form_config"] },
).refine(
  (v) => v.fulfillment_type !== "quote" || !!v.quote_submit_handler,
  { message: "quote_submit_handler is required for quote providers", path: ["quote_submit_handler"] },
).refine(
  (v) => v.fulfillment_type !== "order_api" || !!v.api_endpoint_url,
  { message: "api_endpoint_url is required for order_api providers", path: ["api_endpoint_url"] },
).refine(
  (v) => v.fulfillment_type !== "order_api" || !!v.api_auth_scheme,
  { message: "api_auth_scheme is required for order_api providers", path: ["api_auth_scheme"] },
);

export type ProviderCreateInput = z.infer<typeof providerCreateSchema>;

// PATCH accepts any subset; cross-field validation rerun only if the relevant fields are being touched.
export const providerUpdateSchema = z.object({
  name: z.string().min(1).max(200).optional(),
  slug: z.string().max(120).optional(),
  fulfillment_type: fulfillmentTypeSchema.optional(),
  logo_url: emptyToNull,
  description: z.preprocess(
    (v) => (typeof v === "string" && v.trim() === "" ? null : v),
    z.string().max(5000).nullish(),
  ),
  discount_blurb: z.preprocess(
    (v) => (typeof v === "string" && v.trim() === "" ? null : v),
    z.string().max(200).nullish(),
  ),
  support_email: emptyToNull,
  fulfillment_email: emptyToNull,
  // 103 -- custom phrasing for the 'fulfilled' status-change email.
  fulfilled_email_copy: z.preprocess(
    (v) => (typeof v === "string" && v.trim() === "" ? null : v),
    z.string().max(2000).nullish(),
  ),
  external_url_template: z.preprocess(
    (v) => (typeof v === "string" && v.trim() === "" ? null : v),
    httpUrl(2000).nullish(),
  ),
  external_cta_label: z.preprocess(
    (v) => (typeof v === "string" && v.trim() === "" ? null : v),
    z.string().max(200).nullish(),
  ),
  // 093 -- quote fields. Validation-only when provided; cross-field refinement handled in the API route on the merged record.
  quote_form_config: quoteFormConfigSchema.nullish(),
  quote_submit_handler: z.preprocess(
    (v) => (typeof v === "string" && v.trim() === "" ? null : v),
    z.string().max(100).nullish(),
  ),
  // 095 -- order_api fields; route enforces cross-field presence on the merged record.
  api_endpoint_url: z.preprocess(
    (v) => (typeof v === "string" && v.trim() === "" ? null : v),
    httpUrl(2000).nullish(),
  ),
  api_auth_scheme: apiAuthSchemeSchema.nullish(),
  fulfilment_fallback_email: emptyToNull,
  active: z.boolean().optional(),
  sort_order: z.number().int().min(0).max(9999).optional(),
});

export type ProviderUpdateInput = z.infer<typeof providerUpdateSchema>;

export const productCreateSchema = z.object({
  sku: z.string().min(1).max(100),
  name: z.string().min(1).max(200),
  description: z.preprocess(
    (v) => (typeof v === "string" && v.trim() === "" ? null : v),
    z.string().max(2000).nullish(),
  ),
  image_url: emptyToNull,
  price_cents: z.number().int().min(0).max(1_000_000_00),
  currency: z.string().length(3).optional(),
  // 094 -- per-buyer discount percentages, 0-100. null means no discount.
  discount_pct_parent: z.number().int().min(0).max(100).nullish(),
  discount_pct_school: z.number().int().min(0).max(100).nullish(),
  active: z.boolean().optional(),
  sort_order: z.number().int().min(0).max(9999).optional(),
});

export type ProductCreateInput = z.infer<typeof productCreateSchema>;

export const productUpdateSchema = productCreateSchema.partial();

export type ProductUpdateInput = z.infer<typeof productUpdateSchema>;

// 095 -- Secrets upsert payload. Submitted in plaintext over TLS; server
// encrypts + stores ciphertext. Empty value deletes the secret.
export const providerSecretUpsertSchema = z.object({
  name: z.string().min(1).max(100).regex(/^[a-z0-9_]+$/i, "Name must be alphanumeric + underscore"),
  value: z.string().max(10_000),
});
export type ProviderSecretUpsertInput = z.infer<typeof providerSecretUpsertSchema>;

// 096 -- Cashback schemas.

// Parent initiates a link with the provider. The portal-hosted mock form
// for Elegant Fuels captures external_account_ref; a real OAuth flow will
// provide one server-side, but the endpoint contract is unchanged.
export const linkAccountCallbackSchema = z.object({
  state: z.string().min(1),
  external_account_ref: z.string().min(1).max(200),
});
export type LinkAccountCallbackInput = z.infer<typeof linkAccountCallbackSchema>;

export const cashbackSplitUpsertSchema = z.object({
  parent_pct: z.number().int().min(0).max(100),
  school_pct: z.number().int().min(0).max(100),
  dugads_pct: z.number().int().min(0).max(100),
}).refine(
  (v) => v.parent_pct + v.school_pct + v.dugads_pct === 100,
  { message: "Percentages must sum to exactly 100", path: ["parent_pct"] },
);
export type CashbackSplitUpsertInput = z.infer<typeof cashbackSplitUpsertSchema>;

export const cashbackIngestEventSchema = z.object({
  external_account_ref: z.string().min(1).max(200),
  external_event_ref: z.string().min(1).max(200),
  spend_cents: z.number().int().min(1).max(1_000_000_00),
  currency: z.string().length(3).optional(),
  reported_at: z.string().datetime(),
});

export const cashbackBatchIngestSchema = z.object({
  source: z.enum(["manual_json", "csv_upload", "api_feed"]),
  events: z.array(cashbackIngestEventSchema).min(1, "At least one event is required").max(5000),
});
export type CashbackBatchIngestInputParsed = z.infer<typeof cashbackBatchIngestSchema>;

// Admin marks a single accrual reversed.
export const cashbackAccrualReverseSchema = z.object({
  reason: z.string().min(1, "A reason is required").max(500),
});
export type CashbackAccrualReverseInput = z.infer<typeof cashbackAccrualReverseSchema>;
